import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ColumnCreationFlow, DashboardInstallationColumn, KanbanBoardStore } from '@store';
import { SortOrder } from '@vi-iot/client';
import { ImmediateStateMatcher, uniqueNameValidator } from '@common';
import { CommonStandaloneModule } from '../../../../shared/common-standalone.module';

@Component({
  selector: 'app-kanban-column-name-input',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './kanban-column-name-input.component.html',
  styleUrls: ['./kanban-column-name-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KanbanColumnNameInputComponent implements OnInit, AfterViewInit {
  @ViewChild('columnNameInput') private inputElement: ElementRef;
  @Input() column: DashboardInstallationColumn;
  @Input() allColumnNames: string[];
  matcher = new ImmediateStateMatcher();
  readonly MAX_COLUMN_NAME_LENGTH = 40;
  private columnNameBeforeChange: string;
  private readonly ESCAPE_CODE = 'Escape';
  private readonly ENTER_CODE = 'Enter';
  private columnCreationFlow: ColumnCreationFlow;

  nameForm: FormControl<string>;

  get newName(): string {
    return this.nameForm.value;
  }

  constructor(private kanbanBoardStore: KanbanBoardStore) {}

  ngOnInit(): void {
    this.columnNameBeforeChange = this.column.name;
    const columnNamesWithoutCurrentColumn = this.allColumnNames.filter(
      (name) => name !== this.column.name
    );
    this.nameForm = new FormControl(this.column.name, [
      Validators.required,
      uniqueNameValidator(columnNamesWithoutCurrentColumn),
    ]);
    this.kanbanBoardStore.columnCreationFlow$.pipe(take(1)).subscribe((columnCreationFlow) => {
      this.columnCreationFlow = columnCreationFlow;
    });
  }

  ngAfterViewInit(): void {
    this.inputElement.nativeElement.focus();
  }

  onKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case this.ESCAPE_CODE:
        this.destroyInputComponent();
        break;
      case this.ENTER_CODE:
        if (this.isFormValid()) {
          this.saveNewColumnName();
        }
        break;
    }
  }

  onBlurEvent(): void {
    if (this.isFormValid()) {
      this.saveNewColumnName();
    } else {
      this.destroyInputComponent();
    }
  }

  private saveNewColumnName(): void {
    if (this.columnNameBeforeChange === '') {
      switch (this.columnCreationFlow) {
        case ColumnCreationFlow.Add:
          this.addNewColumn();
          break;
        case ColumnCreationFlow.AddAndCopy:
          this.kanbanBoardStore.createNewColumnAndCopyInstallationToIt(this.newName.trim());
          break;
        case ColumnCreationFlow.AddAndMove:
          this.kanbanBoardStore.createNewColumnAndMoveInstallationToIt(this.newName.trim());
          break;
      }
    } else {
      const { installations, leftNeighbor, sortOrder, version } = this.column;
      const name = this.newName.trim();
      const installationIds = installations.map((installation) => installation.id);
      this.kanbanBoardStore.editColumnName(this.column.id, {
        leftNeighbor,
        name,
        installations: installationIds,
        sortOrder,
        version,
      });
    }
  }

  private addNewColumn(): void {
    const name = this.newName.trim();
    this.kanbanBoardStore.deleteTempColumn();
    this.kanbanBoardStore.createColumn(name, SortOrder.asc);
  }

  private destroyInputComponent(): void {
    if (this.column.isTemporary) {
      this.kanbanBoardStore.deleteTempColumn();
    } else {
      this.kanbanBoardStore.manageColumnNameInput(this.column.id, false);
    }
  }

  private isFormValid(): boolean {
    return this.nameForm.valid && this.columnNameBeforeChange.trim() !== this.nameForm.value.trim();
  }
}
